import { usePriceFormat } from '@/hooks/usePriceFormat';
import {
    Badge,
    Box,
    Flex,
    Icon,
    Td,
    Text
} from '@chakra-ui/react';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CustomLinkOverlay, Ellipsis } from 'BrokerTsLib/src/components';
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip';
import type { InsuranceParamsGridItem, InsuranceParamsVehicleGridItem } from 'BrokerTsLib/src/dtos';
import { formatPersonFullName, formatVehicle } from 'BrokerTsLib/src/dtos';
import type { IOfferCompany } from 'BrokerTsLib/src/enums';
import { ContractAcceptationType, getContractAcceptationTypeName, getInsuranceParamsTypeColor, getPolicyStatusName, InsuranceParamsType, PolicyStatusType, Product } from 'BrokerTsLib/src/enums';

import { InsuranceParamsTypeBadge } from '@/components/InsuranceParamsTypeBadge';
import { VehicleLogo } from '@/components/VehicleLogo';
import { useGetInsuranceParamsNavigationTarget } from '@/hooks/useInsuranceParams';
import { useGetInsuranceParamsTypes } from '@/hooks/useInsuranceParamsTypes';

import { faDollarSign, faFileSignature, faQrcode } from '@fortawesome/pro-duotone-svg-icons';
import { formatDate } from 'date-fns';
import { CompanyLogo } from '../../components/CompanyLogo';
import { TableData, TableRow } from '../../components/Grid';
import { useApp } from '../../hooks/useApp';

const getPolicyStatusColor = (status?: PolicyStatusType) => {
  switch (status) {
    case PolicyStatusType.Active:
    case PolicyStatusType.Created:
      return 'green'
    case PolicyStatusType.Cancelled:
      return 'red'
    case PolicyStatusType.PendingCancellation:
    case PolicyStatusType.CancellationInProgress:
    case PolicyStatusType.PendingActivation:
    case PolicyStatusType.ActivationInProgress:
      return 'orange'
    default:
      return 'gray'
  }
}

export const ContractRow = ({ item }: { item: InsuranceParamsGridItem }) => {
  const { product } = useApp()
  const { formatPrice } = usePriceFormat()
  const { insuranceParamsId: id } = item

  let vehicleItem: InsuranceParamsVehicleGridItem | null = null
  if ([Product.VehicleInsurance, Product.VehicleLeasingInsurance].includes(product!))
    vehicleItem = item as InsuranceParamsVehicleGridItem

  let insurer: IOfferCompany | undefined
  if (item.insurerId)
    insurer = { id: item.insurerId, name: item.insurerFullName! }

  const insureeName = formatPersonFullName(product == Product.VehicleLeasingInsurance ? item.client : item.policyHolder)
  const subject = formatVehicle(vehicleItem?.vehicle)
  const brokerFullName = `${item.createdBy?.name ?? ''} ${item.createdBy?.surname ?? ''}`.trim()

  const showLogo = [Product.VehicleInsurance, Product.VehicleLeasingInsurance].includes(product!)

  const navigateTarget = useGetInsuranceParamsNavigationTarget(item)

  const acceptationTypeIcon: IconProp = item.contractAcceptationType === ContractAcceptationType.Payment || !item.contractAcceptationType ? faDollarSign :
    item.contractAcceptationType === ContractAcceptationType.Signature ? faFileSignature : faQrcode

  const insuranceParamsTypes = useGetInsuranceParamsTypes()

  const isStock = item.type === InsuranceParamsType.StockBefore || item.type === InsuranceParamsType.StockAfter
  const stockRowBg = isStock ? getInsuranceParamsTypeColor(item.type)?.[0] : undefined

  return (
    <CustomLinkOverlay as={TableRow} to={navigateTarget} bg={stockRowBg} textDecoration={item.contractCanceled ? 'line-through' : 'none'}>
      <TableData>
        <Flex gap={2} alignItems='center'>
          {insuranceParamsTypes.length > 1 &&
            <InsuranceParamsTypeBadge type={item.type} offerType={item.offerType} />
          }
          <Text sx={{ color: 'primary.500', fontWeight: 'semibold', fontSize: 'lg' }}>
            {item.contractNumber}
          </Text>
        </Flex>
      </TableData>

      <TableData>
        {item.policyStatus !== undefined && (
          <Flex justifyContent='center'>
            <Badge colorScheme={getPolicyStatusColor(item.policyStatus)}>
              {getPolicyStatusName(item.policyStatus)}
            </Badge>
          </Flex>
        )}
      </TableData>

      <TableData>
        <Text fontWeight='semibold' fontSize='md'>{id}</Text>
      </TableData>

      <Td w={0}>
        <Flex justifyContent='space-between' alignItems='center'>
          <Box>
            {item.contractCreatedDate &&
              <Flex gap={2} alignItems='center'>
                <Text sx={{ fontSize: 'sm' }}>
                  {formatDate(item.contractCreatedDate, 'd.M.yyyy')}
                </Text>
              </Flex>
            }
            {item.startDate && (
              <Text sx={{ fontSize: 'sm', fontWeight: 'semibold' }}>
                {formatDate(item.startDate, 'd.M.yyyy')}
              </Text>
            )}
          </Box>
          <CustomTooltip label={getContractAcceptationTypeName(item.contractAcceptationType)} hasArrow>
            <Icon as={FontAwesomeIcon} icon={acceptationTypeIcon} size='xl' />
          </CustomTooltip>
        </Flex>
      </Td>

      <TableData>
        {showLogo && <VehicleLogo druh={vehicleItem?.vehicle?.druh} znacka={vehicleItem?.vehicle?.znacka} mx='auto' />}
      </TableData>

      <TableData>
        {insureeName && <Text sx={{ fontSize: 'sm' }}>{insureeName}</Text>}


        {!!subject &&
          <Text
            sx={{
              fontWeight: 'semibold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subject}
          </Text>
        }
      </TableData>

      <TableData>
        {insurer && <CompanyLogo
          insurer={insurer} offerType={item.offerType} width="64px" maxH="48px" />}
      </TableData>

      <TableData>
        {item.price && <Text fontWeight='semibold' textAlign='right'>
          {formatPrice({ price: item.price })}
        </Text>}
      </TableData>

      <TableData>
        {item.name && (
          <Ellipsis w={32} color='gray.600' fontSize='sm' whiteSpace='normal'>
            {item.name}
          </Ellipsis>
        )}
        {!item.name && (
          <Text sx={{ color: 'gray.400', fontSize: 'xs' }} className="contract__name">Nezadáno</Text>
        )}
        {brokerFullName && (
          <Text sx={{
            color: 'primary.500',
            fontSize: 'sm',
            fontWeight: 'semibold',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {brokerFullName}
          </Text>
        )}
      </TableData>
    </CustomLinkOverlay >
  )
}
